/** Beat Shop — Browse (Shop tab). Visual parity with the prototype, real data. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as api from '../api';
import { usePreviewPlayer } from '../PreviewPlayer';
import { Beat, CatalogStats, Currency, SortKey, fmtPrice } from '../types';
import {
  BLUE, BeatThumb, Dropdown, EmptyState, HeartIcon, MenuItem, NewBadge, PageShell,
  GhostBtn, PrimaryBtn, SkeletonRows, StatCard, fmtCount, genBars, s, seedFromId, segOff, segOn,
} from '../ui';
import { ShopTabs } from '../components/ShopTabs';
import { trackBeatFavorited, trackBeatshopView } from '../analytics';
import { BEATSHOP_GENRES, BEATSHOP_KEYS } from '../taxonomy';

const TEMPO_MIN = 60;
const TEMPO_MAX = 180;
const PAGE_SIZE = 25;
const sortLabels: Record<SortKey, string> = { newest: 'Newest', oldest: 'Oldest', az: 'A to Z', za: 'Z to A' };

function initialCurrency(): Currency {
  const stored = localStorage.getItem('beatshop-currency') as Currency | null;
  if (stored === 'cash') return 'cash';
  // Points tender is coming soon — prefer cash and migrate stale preference.
  if (stored === 'points') localStorage.setItem('beatshop-currency', 'cash');
  return 'cash';
}

const BrowsePage: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const player = usePreviewPlayer();
  const [beats, setBeats] = useState<Beat[] | null>(null);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<CatalogStats | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [view, setView] = useState<'list' | 'grid'>('list');
  const [currency, setCurrency] = useState<Currency>(initialCurrency);
  const [page, setPage] = useState(0);

  const q = params.get('q') || '';
  const tab = (params.get('tab') as 'all' | 'favorites') || 'all';
  const sort = (params.get('sort') as SortKey) || 'newest';
  const genres = useMemo(() => (params.get('genres') || '').split(',').filter(Boolean), [params]);
  const keys = useMemo(() => (params.get('keys') || '').split(',').filter(Boolean), [params]);
  const bpmMin = Number(params.get('bpm_min')) || TEMPO_MIN;
  const bpmMax = Number(params.get('bpm_max')) || TEMPO_MAX;

  const setParam = useCallback((key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key); else next.set(key, value);
    setParams(next, { replace: true });
  }, [params, setParams]);

  const setCur = (c: Currency) => {
    if (c === 'points') return; // coming soon
    setCurrency(c);
    localStorage.setItem('beatshop-currency', c);
  };

  useEffect(() => { api.getStats().then(setStats).catch(() => setStats(null)); trackBeatshopView('shop'); }, []);

  // Reset pagination whenever filters/search change.
  useEffect(() => {
    setPage(0);
  }, [q, tab, sort, genres, keys, bpmMin, bpmMax]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      const query: Record<string, string> = {
        sort,
        tab,
        limit: String(PAGE_SIZE),
        offset: String(page * PAGE_SIZE),
      };
      if (q) query.q = q;
      if (genres.length) query.genres = genres.join(',');
      if (keys.length) query.keys = keys.join(',');
      if (bpmMin > TEMPO_MIN) query.bpm_min = String(bpmMin);
      if (bpmMax < TEMPO_MAX) query.bpm_max = String(bpmMax);
      api.getCatalog(query)
        .then((r) => {
          if (!cancelled) {
            setBeats(r.beats);
            setTotal(r.total || 0);
          }
        })
        .catch(() => { if (!cancelled) { setBeats([]); setTotal(0); } });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, tab, sort, genres, keys, bpmMin, bpmMax, page]);

  // Keep mini-player queue in sync with the filtered Shop list.
  useEffect(() => {
    if (!beats) {
      player.setQueue([]);
      return;
    }
    player.setQueue(beats.map((b) => ({
      id: b.id,
      name: b.name,
      preview_url: b.preview_url || '',
      bpm: b.bpm,
      musical_key: b.musical_key,
      seller_name: b.seller_name,
      is_favorited: b.is_favorited,
    })));
  }, [beats, player.setQueue]);

  const toggleFav = async (beat: Beat, e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !beat.is_favorited;
    setBeats((prev) => prev ? prev.map((b) => b.id === beat.id
      ? { ...b, is_favorited: next, favorite_count: b.favorite_count + (next ? 1 : -1) } : b) : prev);
    player.setFavorited(beat.id, next);
    try { await api.setFavorite(beat.id, next); trackBeatFavorited(beat.id, next); } catch { /* optimistic; refetch on nav */ }
  };

  const playPreview = (beat: Beat, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!beat.preview_url) return;
    player.toggle({
      id: beat.id,
      name: beat.name,
      preview_url: beat.preview_url,
      bpm: beat.bpm,
      musical_key: beat.musical_key,
      seller_name: beat.seller_name,
      is_favorited: beat.is_favorited,
    });
  };

  const toggleMulti = (key: 'genres' | 'keys', list: string[], value: string) => {
    const next = list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
    setParam(key, next.join(','));
  };

  const tempoActive = bpmMin > TEMPO_MIN || bpmMax < TEMPO_MAX;
  const genreLabel = genres.length === 0 ? 'Genre' : genres.length === 1 ? genres[0] : `${genres.length} genres`;
  const keyLabel = keys.length === 0 ? 'Key' : keys.length === 1 ? keys[0] : `${keys.length} keys`;
  const tempoLabel = tempoActive ? `${bpmMin}–${bpmMax} BPM` : 'Tempo';
  const tPct = (v: number) => ((v - TEMPO_MIN) / (TEMPO_MAX - TEMPO_MIN)) * 100;

  const hasResults = !!beats && beats.length > 0;
  const iconSeg = 'display:flex;align-items:center;justify-content:center;padding:6px 8px;border-radius:7px;cursor:pointer;border:none;transition:all .12s;';
  const playBtn = (active: boolean) =>
    s('display:flex;align-items:center;justify-content:center;width:34px;height:34px;border-radius:50%;border:none;cursor:pointer;flex-shrink:0;' +
      (active ? 'background:#2F6BFF;color:#fff' : 'background:var(--bs-btn-bg);color:var(--bs-btn-text)'));

  const PlayGlyph: React.FC<{ playing: boolean }> = ({ playing }) => (
    playing ? (
      <svg width="13" height="13" viewBox="0 0 14 14" fill="currentColor"><rect x="2.5" y="2" width="3" height="10" rx="1" /><rect x="8.5" y="2" width="3" height="10" rx="1" /></svg>
    ) : (
      <svg width="13" height="13" viewBox="0 0 14 14" fill="currentColor"><path d="M3 2v10l9-5z" /></svg>
    )
  );

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const lastPage = pageCount - 1;

  const pagesWindow = (current: number, totalPages: number) => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
    const out: (number | '…')[] = [];
    const push = (v: number | '…') => { if (out.length === 0 || out[out.length - 1] !== v) out.push(v); };

    push(0);
    const start = Math.max(1, current - 1);
    const end = Math.min(totalPages - 2, current + 1);
    if (start > 1) push('…');
    for (let i = start; i <= end; i++) push(i);
    if (end < totalPages - 2) push('…');
    push(totalPages - 1);
    return out;
  };

  return (
    <PageShell>
      <ShopTabs active="shop" />
      <div style={s('font-size:11.5px;font-weight:600;letter-spacing:0.15em;color:var(--bs-text-label);margin-bottom:8px')}>EXCLUSIVE INSTRUMENTALS</div>
      <h1 style={s('margin:0;font-weight:700;font-size:40px;letter-spacing:-0.025em;line-height:1.15;color:var(--bs-text)')}>Beat Shop</h1>
      <p style={s('margin:8px 0 26px;color:var(--bs-text-muted);font-size:14.5px;line-height:1.5')}>
        Own the master — every beat ships with full stems, copyrights and credits{stats ? ` · ${stats.live_now} live now` : ''}
      </p>

      {/* stat cards — New Drops ↔ Points icons swapped per client request */}
      <div className="bs-stats" style={s('display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:34px')}>
        <StatCard label="LIVE NOW" value={stats ? stats.live_now : '—'} sub="exclusive instrumentals"
          icon={<><circle cx="9" cy="9" r="6" /><circle cx="9" cy="9" r="1.4" /></>} />
        <StatCard label="NEW DROPS" value={stats ? stats.new_this_week : '—'} sub="↑ this week" subColor="var(--bs-green)"
          icon={<><circle cx="9" cy="9" r="6" /><path d="M9 6v6M7 7.5h2.5a1.3 1.3 0 0 1 0 2.6H7" /></>} />
        <StatCard label="YOUR POINTS"
          value={stats ? <>{Number(stats.points_balance).toLocaleString('en-US')} <span style={s('color:#2F6BFF;font-size:20px')}>✦</span></> : '—'}
          sub={stats ? `≈ ${stats.approx_beats_affordable} beats` : ''}
          icon={<path d="M9 2.5l1.8 4.7L15.5 9l-4.7 1.8L9 15.5l-1.8-4.7L2.5 9l4.7-1.8z" strokeLinejoin="round" />} />
        <StatCard label="EXCLUSIVE LICENCE FEE" value="$250" sub="+ 5% of record"
          icon={<><path d="M8.5 2.5H14a1.5 1.5 0 0 1 1.5 1.5v5.5L8.5 16 2.5 10 8.5 2.5z" strokeLinejoin="round" /><circle cx="11.5" cy="6" r="1" /></>} />
      </div>

      {/* library title + toggles */}
      <div style={s('display:flex;align-items:center;justify-content:space-between;margin-bottom:18px;flex-wrap:wrap;gap:14px')}>
        <h2 style={s('margin:0;font-weight:700;font-size:21px;letter-spacing:-0.01em')}>{tab === 'favorites' ? 'Favorites' : 'The Library'}</h2>
        <div style={s('display:flex;align-items:center;gap:10px;flex-wrap:wrap')}>
          <div style={s('flex:1;display:flex;align-items:center;gap:10px;padding:9px 14px;background:var(--bs-surface);border:1px solid var(--bs-border);border-radius:999px;min-width:230px')}>
            <svg width="15" height="15" viewBox="0 0 18 18" fill="none" stroke="var(--bs-text-label)" strokeWidth="1.6" strokeLinecap="round"><circle cx="8" cy="8" r="5" /><line x1="11.8" y1="11.8" x2="15.5" y2="15.5" /></svg>
            <input value={q} onChange={(e) => setParam('q', e.target.value)} placeholder="Search beats by name…"
              style={s('flex:1;background:none;border:none;outline:none;color:var(--bs-text);font-size:13.5px')} />
          </div>
          <div style={s('display:flex;background:var(--bs-surface);border:1px solid var(--bs-border);border-radius:9px;padding:3px;gap:2px')}>
            <button onClick={() => setParam('tab', null)} style={s(tab === 'all' ? segOn : segOff)}>All</button>
            <button onClick={() => setParam('tab', 'favorites')} style={s(tab === 'favorites' ? segOn : segOff)}>
              <span style={s('display:inline-flex;align-items:center;gap:5px')}><HeartIcon filled={tab === 'favorites'} size={13} stroke="currentColor" />Favorites</span>
            </button>
          </div>
          <div style={s('display:flex;background:var(--bs-surface);border:1px solid var(--bs-border);border-radius:9px;padding:3px;gap:2px')} title="Nova Points coming soon">
            <button type="button" disabled style={s(segOff + 'opacity:0.45;cursor:not-allowed')}>Points · soon</button>
            <button type="button" onClick={() => setCur('cash')} style={s(currency === 'cash' ? segOn : segOff)}>Cash</button>
          </div>
        </div>
      </div>

      {/* filters */}
      <div style={s('display:flex;align-items:center;justify-content:flex-end;margin-bottom:14px;flex-wrap:wrap;gap:9px')}>
        <div style={s('margin-right:auto;font-size:13px;font-weight:600;color:var(--bs-text-label);letter-spacing:0.04em')}>Filter by</div>
        <div style={s('display:flex;align-items:center;gap:9px;position:relative;flex-wrap:wrap')}>
          <Dropdown label={genreLabel} open={openMenu === 'genre'} onToggle={() => setOpenMenu(openMenu === 'genre' ? null : 'genre')} minWidth={170} maxHeight={300}>
            <MenuItem active={genres.length === 0} onSelect={() => { setParam('genres', null); setOpenMenu(null); }}>All genres</MenuItem>
            {BEATSHOP_GENRES.map((g) => (
              <MenuItem key={g} active={genres.includes(g)} onSelect={() => toggleMulti('genres', genres, g)}>
                {(genres.includes(g) ? '✓  ' : '    ') + g}
              </MenuItem>
            ))}
          </Dropdown>
          {/* tempo */}
          <div className="bs-dropdown-wrap" style={s('position:relative')}>
            <button onClick={() => setOpenMenu(openMenu === 'tempo' ? null : 'tempo')} style={s('display:flex;align-items:center;gap:7px;padding:8px 13px;background:var(--bs-surface);border:1px solid var(--bs-border);border-radius:9px;font-weight:500;font-size:13px;color:var(--bs-text-body);cursor:pointer')}>
              {tempoLabel} <span style={s('color:var(--bs-text-faint);font-size:9px')}>▾</span>
            </button>
            {openMenu === 'tempo' && (
              <div className="bs-dropdown-panel bs-tempo-panel" style={s('position:absolute;right:0;top:38px;z-index:30;width:264px;background:var(--bs-menu);border:1px solid var(--bs-border-2);border-radius:14px;padding:18px 18px 16px;box-shadow:0 16px 44px var(--bs-shadow-strong)')}>
                <div style={s('display:flex;align-items:center;justify-content:space-between;margin-bottom:16px')}>
                  <span style={s('font-size:11px;font-weight:600;letter-spacing:0.08em;color:var(--bs-text-label)')}>TEMPO (BPM)</span>
                  <button onClick={() => { setParam('bpm_min', null); setParam('bpm_max', null); }} style={s('background:none;border:none;color:var(--bs-blue-soft);font-size:12px;cursor:pointer;font-weight:500')}>Reset</button>
                </div>
                <div style={s('display:flex;align-items:center;gap:9px;margin-bottom:20px')}>
                  <div style={s('flex:1;background:var(--bs-well);border:1px solid var(--bs-border-2);border-radius:9px;padding:7px 10px')}>
                    <div style={s('font-size:9px;color:var(--bs-text-dim);letter-spacing:0.06em')}>MIN</div>
                    <div style={s('font-size:15px;font-weight:600;color:var(--bs-text);font-variant-numeric:tabular-nums')}>{bpmMin} BPM</div>
                  </div>
                  <div style={s('width:10px;height:1px;background:var(--bs-border-2)')} />
                  <div style={s('flex:1;background:var(--bs-well);border:1px solid var(--bs-border-2);border-radius:9px;padding:7px 10px')}>
                    <div style={s('font-size:9px;color:var(--bs-text-dim);letter-spacing:0.06em')}>MAX</div>
                    <div style={s('font-size:15px;font-weight:600;color:var(--bs-text);font-variant-numeric:tabular-nums')}>{bpmMax} BPM</div>
                  </div>
                </div>
                <div style={s('position:relative;height:26px;margin:0 2px 6px')}>
                  <div style={s('position:absolute;top:11px;left:0;right:0;height:4px;border-radius:2px;background:var(--bs-border-2)')} />
                  <div style={{ ...s('position:absolute;top:11px;height:4px;border-radius:2px;background:#2F6BFF'), left: `${tPct(bpmMin)}%`, width: `${tPct(bpmMax) - tPct(bpmMin)}%` }} />
                  <input className="nova-range" type="range" min={TEMPO_MIN} max={TEMPO_MAX} step={1} value={bpmMin}
                    onChange={(e) => setParam('bpm_min', String(Math.min(+e.target.value, bpmMax - 1)))} />
                  <input className="nova-range" type="range" min={TEMPO_MIN} max={TEMPO_MAX} step={1} value={bpmMax}
                    onChange={(e) => setParam('bpm_max', String(Math.max(+e.target.value, bpmMin + 1)))} />
                </div>
                <div style={s('display:flex;justify-content:space-between;font-size:10px;color:var(--bs-text-faint);font-variant-numeric:tabular-nums')}>
                  <span>{TEMPO_MIN}</span><span>{TEMPO_MAX}</span>
                </div>
              </div>
            )}
          </div>
          <Dropdown label={keyLabel} open={openMenu === 'key'} onToggle={() => setOpenMenu(openMenu === 'key' ? null : 'key')} minWidth={150} maxHeight={280}>
            <MenuItem active={keys.length === 0} onSelect={() => { setParam('keys', null); setOpenMenu(null); }}>All keys</MenuItem>
            {BEATSHOP_KEYS.map((k) => (
              <MenuItem key={k} active={keys.includes(k)} onSelect={() => toggleMulti('keys', keys, k)}>
                {(keys.includes(k) ? '✓  ' : '    ') + k}
              </MenuItem>
            ))}
          </Dropdown>
          <div style={s('display:flex;background:var(--bs-surface);border:1px solid var(--bs-border);border-radius:9px;padding:3px;gap:2px')}>
            <button onClick={() => setView('list')} style={s(iconSeg + (view === 'list' ? 'background:var(--bs-seg-on-bg);color:var(--bs-seg-on-text)' : 'background:transparent;color:var(--bs-text-label)'))}>
              <svg width="15" height="15" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><line x1="3" y1="5" x2="15" y2="5" /><line x1="3" y1="9" x2="15" y2="9" /><line x1="3" y1="13" x2="15" y2="13" /></svg>
            </button>
            <button onClick={() => setView('grid')} style={s(iconSeg + (view === 'grid' ? 'background:var(--bs-seg-on-bg);color:var(--bs-seg-on-text)' : 'background:transparent;color:var(--bs-text-label)'))}>
              <svg width="15" height="15" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="3" width="5" height="5" rx="1" /><rect x="10" y="3" width="5" height="5" rx="1" /><rect x="3" y="10" width="5" height="5" rx="1" /><rect x="10" y="10" width="5" height="5" rx="1" /></svg>
            </button>
          </div>
        </div>
      </div>

      <div style={s('display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:14px;flex-wrap:wrap')}>
        <div style={s('font-size:13px;color:var(--bs-text-label)')}>{beats ? `${beats.length} beats` : 'Loading…'}</div>
        <div className="bs-sort-wrap" style={s('display:flex;align-items:center;gap:9px;position:relative')}>
          <span style={s('font-size:13px;color:var(--bs-text-label)')}>Sort by</span>
          <Dropdown label={sortLabels[sort]} open={openMenu === 'sort'} onToggle={() => setOpenMenu(openMenu === 'sort' ? null : 'sort')} minWidth={190}>
            {(Object.keys(sortLabels) as SortKey[]).map((v) => (
              <MenuItem key={v} active={sort === v} onSelect={() => { setParam('sort', v === 'newest' ? null : v); setOpenMenu(null); }}>
                {v === 'az' ? 'Alphabetical · A to Z' : v === 'za' ? 'Alphabetical · Z to A' : sortLabels[v]}
              </MenuItem>
            ))}
          </Dropdown>
        </div>
      </div>

      {/* LIST */}
      {beats === null && <SkeletonRows />}
      {hasResults && view === 'list' && (
        <div className="bs-browse-list">
          <div className="bs-browse-list-head">
            <span>BEAT ↓</span>
            <span className="bs-browse-col-desktop">TEMPO</span>
            <span className="bs-browse-col-desktop">KEY</span>
            <span className="bs-browse-col-desktop" style={s('text-align:right')}>PRICE</span>
            <span />
          </div>
          {beats!.map((bt) => {
            const playing = player.isPlayingId(bt.id);
            return (
              <div key={bt.id} className="bs-row bs-browse-list-row" onClick={() => navigate(`/store/beat-shop/beats/${bt.id}`)}>
                <div style={s('display:flex;align-items:center;gap:13px;min-width:0')}>
                  <BeatThumb />
                  <div style={s('min-width:0')}>
                    <div style={s('display:flex;align-items:center;gap:8px;flex-wrap:wrap')}>
                      <span className="bs-browse-beat-name">{bt.name}</span>
                      {bt.is_new && <NewBadge />}
                    </div>
                    <div className="bs-browse-beat-sub">
                      {bt.genres.slice(0, 3).join(' · ')}{bt.genres.length > 3 ? ` +${bt.genres.length - 3}` : ''} · {fmtCount(bt.favorite_count)} ♥
                    </div>
                    <div className="bs-browse-beat-mobile-meta">
                      {bt.bpm} BPM · {bt.musical_key} · {fmtPrice(bt, currency)}
                    </div>
                  </div>
                </div>
                <span className="bs-browse-col-desktop" style={s('font-size:13px;color:var(--bs-text-body);font-variant-numeric:tabular-nums')}>{bt.bpm} BPM</span>
                <span className="bs-browse-col-desktop" style={s('font-size:13px;color:var(--bs-text-body)')}>{bt.musical_key}</span>
                <span className="bs-browse-col-desktop" style={s('font-weight:600;font-size:14px;text-align:right;color:var(--bs-text)')}>{fmtPrice(bt, currency)}</span>
                <div style={s('display:flex;justify-content:flex-end;gap:4px')}>
                  <button
                    type="button"
                    onClick={(e) => playPreview(bt, e)}
                    disabled={!bt.preview_url}
                    aria-label={playing ? `Pause ${bt.name}` : `Play ${bt.name}`}
                    style={{ ...playBtn(playing), width: 30, height: 30, opacity: bt.preview_url ? 1 : 0.4 }}
                  >
                    <PlayGlyph playing={playing} />
                  </button>
                  <button className="bs-heart" onClick={(e) => toggleFav(bt, e)} title="Save to favorites"
                    style={s('display:flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:8px;border:none;background:transparent;cursor:pointer;transition:background .12s')}>
                    <HeartIcon filled={bt.is_favorited} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* GRID */}
      {hasResults && view === 'grid' && (
        <div className="bs-browse-grid" style={s('display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:16px')}>
          {beats!.map((bt) => {
            const playing = player.isPlayingId(bt.id);
            return (
              <div key={bt.id} className="bs-card" onClick={() => navigate(`/store/beat-shop/beats/${bt.id}`)}
                style={s('cursor:pointer;background:var(--bs-surface);border:1px solid var(--bs-border);border-radius:16px;overflow:hidden;transition:border-color .14s,transform .14s')}>
                <div style={s('position:relative;height:128px;background:radial-gradient(120px 80px at 70% 18%, rgba(47,107,255,0.13), transparent),var(--bs-well);display:flex;align-items:flex-end;justify-content:center;padding:18px 16px;border-bottom:1px solid var(--bs-line)')}>
                  <div style={s('display:flex;align-items:flex-end;gap:2px;height:60px;width:84%;max-width:100%;overflow:hidden;min-width:0')}>
                    {genBars(seedFromId(bt.id), 22, 18, 100).map((h, i) => (
                      <div key={i} style={{ ...s('flex:1 1 0;min-width:0;border-radius:2px;background:rgba(111,160,255,0.5)'), height: `${h}%` }} />
                    ))}
                  </div>
                  {bt.is_new && <span style={s('position:absolute;left:12px;top:12px;padding:3px 8px;background:#2F6BFF;color:#fff;font-size:9.5px;font-weight:700;letter-spacing:0.06em;border-radius:6px')}>NEW</span>}
                  <button className="bs-heart" onClick={(e) => toggleFav(bt, e)} title="Save to favorites"
                    style={s('display:flex;align-items:center;justify-content:center;border:none;cursor:pointer;transition:background .12s;position:absolute;right:54px;bottom:12px;width:34px;height:34px;border-radius:50%;background:var(--bs-float-chip);backdrop-filter:blur(4px)')}>
                    <HeartIcon filled={bt.is_favorited} size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => playPreview(bt, e)}
                    disabled={!bt.preview_url}
                    aria-label={playing ? `Pause ${bt.name}` : `Play ${bt.name}`}
                    style={{ ...playBtn(playing), position: 'absolute', right: 12, bottom: 12, opacity: bt.preview_url ? 1 : 0.4 }}
                  >
                    <PlayGlyph playing={playing} />
                  </button>
                </div>
                <div style={s('padding:14px 15px 16px')}>
                  <div style={s('font-weight:600;font-size:17px;color:var(--bs-text)')}>{bt.name}</div>
                  <div style={s('margin-top:4px;font-size:12.5px;color:var(--bs-text-muted)')}>{bt.bpm} BPM · {bt.musical_key}</div>
                  <div style={s('margin-top:8px;font-size:12px;color:var(--bs-text-dim);white-space:nowrap;overflow:hidden;text-overflow:ellipsis')}>
                    {bt.genres.slice(0, 3).join(' · ')} · {fmtCount(bt.favorite_count)} ♥
                  </div>
                  <div style={s('display:flex;align-items:baseline;justify-content:space-between;margin-top:13px;padding-top:12px;border-top:1px solid var(--bs-line)')}>
                    <span style={s('font-weight:600;font-size:15px;color:var(--bs-text)')}>{fmtPrice(bt, currency)}</span>
                    <span style={s('font-size:11px;color:var(--bs-text-faint)')}>+5% record</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* PAGINATION */}
      {beats !== null && (
        <div style={s('display:flex;align-items:center;justify-content:space-between;gap:14px;padding:18px 0 6px;flex-wrap:wrap')}>
          <span style={s('font-size:12.5px;color:var(--bs-text-muted);font-variant-numeric:tabular-nums')}>
            {total === 0
              ? '0 of 0'
              : `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}`}
          </span>
          <div style={s('display:flex;align-items:center;gap:9px')}>
            <GhostBtn
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              style="padding:6px 12px;font-size:12px"
            >
              ← Prev
            </GhostBtn>

            <div style={s('display:flex;align-items:center;gap:6px')}>
              {pagesWindow(page, pageCount).map((v, i) =>
                v === '…' ? (
                  <span key={`e-${i}`} style={s('font-size:12.5px;color:var(--bs-text-label);padding:0 4px')}>…</span>
                ) : (
                  <GhostBtn
                    key={v}
                    onClick={() => setPage(v)}
                    disabled={v === page}
                    style={`padding:6px 10px;font-size:12px;${v === page ? 'opacity:0.7' : ''}`}
                  >
                    {v + 1}
                  </GhostBtn>
                )
              )}
            </div>

            <GhostBtn
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              disabled={page >= lastPage}
              style="padding:6px 12px;font-size:12px"
            >
              Next →
            </GhostBtn>
          </div>
        </div>
      )}

      {/* EMPTY STATES */}
      {beats !== null && !hasResults && tab !== 'favorites' && (
        <EmptyState
          icon={<div style={s('display:flex;align-items:flex-end;gap:3px;height:40px;margin-bottom:20px;opacity:0.45')}>
            {[30, 70, 45, 90, 55].map((h, i) => (<div key={i} style={{ ...s('width:5px;background:var(--bs-wave-off);border-radius:3px'), height: `${h}%` }} />))}
          </div>}
          title="No beats match those filters"
          body="Try widening the genre, tempo or key — the vault has plenty more."
          action={<PrimaryBtn onClick={() => setParams(new URLSearchParams(), { replace: true })}>Clear filters</PrimaryBtn>}
        />
      )}
      {beats !== null && !hasResults && tab === 'favorites' && (
        <EmptyState
          icon={<div style={s('margin-bottom:18px;opacity:0.9')}><HeartIcon filled={false} size={40} stroke={BLUE} /></div>}
          title="No favorites yet"
          body="Tap the heart on any beat to save it here — build a shortlist before you buy."
          action={<PrimaryBtn onClick={() => setParam('tab', null)}>Browse all beats</PrimaryBtn>}
        />
      )}
    </PageShell>
  );
};

export default BrowsePage;
